import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../../BaseUrl';

// Use your BaseUrl configuration
const BASE_URL = `${BaseUrl.templatesurl}InsuranceCompanyMasters`;

// Company Async thunks
export const fetchCompanyMasters = createAsyncThunk(
  'insurance/fetchCompanyMasters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/CompanyMaster`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCompanyById = createAsyncThunk(
  'insurance/fetchCompanyById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/GetCompanyById/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addCompanyMaster = createAsyncThunk(
  'insurance/addCompanyMaster',
  async (companyData, { rejectWithValue }) => {
    try {
      const response = await axios.post(BASE_URL, companyData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCompanyMaster = createAsyncThunk(
  'insurance/updateCompanyMaster',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCompanyMaster = createAsyncThunk(
  'insurance/deleteCompanyMaster',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const insuranceSlice = createSlice({
  name: 'insurance',
  initialState: {
    companies: [],
    selectedCompany: null,
    loading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all companies
      .addCase(fetchCompanyMasters.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchCompanyMasters.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload || [];
      })
      .addCase(fetchCompanyMasters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch companies';
      })
      
      // Fetch company by ID
      .addCase(fetchCompanyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCompany = action.payload;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch company';
      })
      
      // Add company
      .addCase(addCompanyMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addCompanyMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Insurance company added successfully!';
        // Optionally add the new company to the list
        if (action.payload) {
          state.companies.push(action.payload);
        }
      })
      .addCase(addCompanyMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to add company';
      })
      
      // Update company
      .addCase(updateCompanyMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateCompanyMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Insurance company updated successfully!';
        // Optionally update the company in the list
        if (action.payload) {
          const index = state.companies.findIndex(c => c.Id === action.payload.Id);
          if (index !== -1) {
            state.companies[index] = action.payload;
          }
        }
      })
      .addCase(updateCompanyMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update company';
      })
      
      // Delete company
      .addCase(deleteCompanyMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteCompanyMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Insurance company deleted successfully!';
        // Remove the deleted company from the list
        state.companies = state.companies.filter(c => c.Id !== action.payload);
      })
      .addCase(deleteCompanyMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete company';
      });
  },
});

export const { clearError, clearSuccess } = insuranceSlice.actions;
export default insuranceSlice.reducer;